/**
 * wishlist-sh.js
 * Category-based LocalStorage wishlist.
 * data shape: { "Category Name": [item, item, ...], ... }
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'wishlist_sh_items';
  var pendingItem = null;
  var activeCategory = null;

  /* ---------------- storage helpers ---------------- */

  function getWishlistData() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      var data = raw ? JSON.parse(raw) : {};
      if (Array.isArray(data)) {
        data = data.length ? { 'My Wishlist': data } : {};
      }
      return data;
    } catch (e) {
      return {};
    }
  }

  function saveWishlistData(data) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    document.dispatchEvent(new CustomEvent('wishlist-sh:updated', { detail: { data: data } }));
  }

  function getCategoryItems(name) {
    return getWishlistData()[name] || [];
  }

  function getTotalCount() {
    var data = getWishlistData();
    var count = 0;
    Object.keys(data).forEach(function (cat) { count += data[cat].length; });
    return count;
  }

  function findItemCategory(id) {
    var data = getWishlistData();
    var found = null;
    Object.keys(data).forEach(function (cat) {
      if (data[cat].some(function (i) { return String(i.id) === String(id); })) found = cat;
    });
    return found;
  }

  function isInWishlist(id) {
    return !!findItemCategory(id);
  }

  function createCategory(name) {
    var data = getWishlistData();
    if (!data[name]) {
      data[name] = [];
      saveWishlistData(data);
    }
  }

  function addToCategory(categoryName, item) {
    var data = getWishlistData();
    if (!data[categoryName]) data[categoryName] = [];
    if (!data[categoryName].some(function (i) { return String(i.id) === String(item.id); })) {
      data[categoryName].push(item);
    }
    saveWishlistData(data);
  }

  function removeFromWishlist(id) {
    var data = getWishlistData();
    Object.keys(data).forEach(function (cat) {
      data[cat] = data[cat].filter(function (i) { return String(i.id) !== String(id); });
    });
    saveWishlistData(data);
  }

  function deleteCategory(name) {
    var data = getWishlistData();
    delete data[name];
    saveWishlistData(data);
  }

  function renameCategory(oldName, newName) {
    var data = getWishlistData();
    if (!data[oldName] || data[newName]) return false;
    data[newName] = data[oldName];
    delete data[oldName];
    saveWishlistData(data);
    return true;
  }

  /* ---------------- header badge ---------------- */

  function updateCountBadges() {
    var count = getTotalCount();
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) { el.textContent = count; });
    document.querySelectorAll('[data-header-sh-icon="wishlist"] [data-wishlist-count]').forEach(function (el) {
      el.hidden = count === 0;
    });
  }

  /* ---------------- product card heart state ---------------- */

  function syncCardStates() {
    document.querySelectorAll('[data-wishlist-card]').forEach(function (card) {
      var id = card.getAttribute('data-product-id');
      var btn = card.querySelector('[data-wishlist-toggle]');
      if (!btn) return;
      btn.classList.toggle('is-active', isInWishlist(id));
    });
  }

  /* ---------------- add-to-list modal ---------------- */

  function buildModal() {
    if (document.querySelector('[data-wishlist-modal]')) return;
    var modal = document.createElement('div');
    modal.setAttribute('data-wishlist-modal', '');
    modal.className = 'wishlistsh-modal';
    modal.innerHTML =
      '<div class="wishlistsh-modal__overlay" data-wishlist-modal-close></div>' +
      '<div class="wishlistsh-modal__box">' +
        '<div class="wishlistsh-modal__header">' +
          '<p>Add to wishlist</p>' +
          '<button type="button" class="wishlistsh-modal__close" data-wishlist-modal-close aria-label="Close">&times;</button>' +
        '</div>' +
        '<div class="wishlistsh-modal__body">' +
          '<p class="wishlistsh-modal__label">Select list</p>' +
          '<div class="wishlistsh-modal__list" data-wishlist-modal-list></div>' +
          '<div class="wishlistsh-modal__new">' +
            '<button type="button" class="wishlistsh-modal__new-btn" data-wishlist-modal-new-btn>+ New list</button>' +
            '<div class="wishlistsh-modal__new-form" data-wishlist-modal-new-form hidden>' +
              '<input type="text" placeholder="List name" data-wishlist-modal-new-input maxlength="40">' +
              '<button type="button" class="wishlistsh-modal__new-confirm" data-wishlist-modal-new-confirm>Add</button>' +
            '</div>' +
          '</div>' +
        '</div>' +
      '</div>';
    document.body.appendChild(modal);
  }

  function renderModalList() {
    var listEl = document.querySelector('[data-wishlist-modal-list]');
    if (!listEl) return;
    var data = getWishlistData();
    var categories = Object.keys(data);

    if (!categories.length) {
      listEl.innerHTML = '<p class="wishlistsh-modal__empty">No lists yet — create one below.</p>';
      return;
    }

    listEl.innerHTML = categories.map(function (cat) {
      return '<button type="button" class="wishlistsh-modal__item" data-wishlist-modal-select="' + cat.replace(/"/g, '&quot;') + '">' +
        '<span class="wishlistsh-modal__item-name">' + cat + '</span>' +
        '<span class="wishlistsh-modal__item-count">' + data[cat].length + ' item' + (data[cat].length === 1 ? '' : 's') + '</span>' +
      '</button>';
    }).join('');
  }

  function openModal(item) {
    pendingItem = item;
    buildModal();
    renderModalList();
    var form = document.querySelector('[data-wishlist-modal-new-form]');
    var btn = document.querySelector('[data-wishlist-modal-new-btn]');
    if (form) form.hidden = true;
    if (btn) btn.hidden = false;
    document.querySelector('[data-wishlist-modal]').classList.add('is-open');
    document.body.classList.add('wishlistsh-modal-open');
  }

  function closeModal() {
    var modal = document.querySelector('[data-wishlist-modal]');
    if (modal) modal.classList.remove('is-open');
    document.body.classList.remove('wishlistsh-modal-open');
    pendingItem = null;
  }

  /* ---------------- generic dialog (replaces prompt / confirm) ---------------- */

  var dialogCallback = null;
  var dialogHasInput = false;

  function buildDialog() {
    if (document.querySelector('[data-wishlist-dialog]')) return;
    var dialog = document.createElement('div');
    dialog.setAttribute('data-wishlist-dialog', '');
    dialog.className = 'wishlistsh-modal';
    dialog.innerHTML =
      '<div class="wishlistsh-modal__overlay" data-wishlist-dialog-close></div>' +
      '<div class="wishlistsh-modal__box">' +
        '<div class="wishlistsh-modal__header">' +
          '<p data-wishlist-dialog-title></p>' +
          '<button type="button" class="wishlistsh-modal__close" data-wishlist-dialog-close aria-label="Close">&times;</button>' +
        '</div>' +
        '<div class="wishlistsh-modal__body">' +
          '<p class="wishlistsh-dialog__message" data-wishlist-dialog-message hidden></p>' +
          '<div data-wishlist-dialog-field hidden>' +
            '<p class="wishlistsh-modal__label" data-wishlist-dialog-label></p>' +
            '<input type="text" class="wishlistsh-dialog__input" data-wishlist-dialog-input maxlength="40">' +
            '<p class="wishlistsh-dialog__error" data-wishlist-dialog-error hidden>Please enter a name.</p>' +
          '</div>' +
          '<div class="wishlistsh-dialog__actions">' +
            '<button type="button" class="wishlistsh-dialog__btn" data-wishlist-dialog-close>Cancel</button>' +
            '<button type="button" class="wishlistsh-dialog__btn wishlistsh-dialog__btn--confirm" data-wishlist-dialog-confirm>OK</button>' +
          '</div>' +
        '</div>' +
      '</div>';
    document.body.appendChild(dialog);
  }

  /*
    openDialog({
      title: 'New list',
      message: 'Optional text',        // shown above the input
      label: 'List name',              // only used when input is true
      input: true,                     // false = confirm-style dialog
      value: '',                       // prefilled input value
      confirmText: 'Create',
      onConfirm: function (value) {}
    })
  */
  function openDialog(opts) {
    buildDialog();
    var dialog = document.querySelector('[data-wishlist-dialog]');
    var msgEl = dialog.querySelector('[data-wishlist-dialog-message]');
    var field = dialog.querySelector('[data-wishlist-dialog-field]');
    var input = dialog.querySelector('[data-wishlist-dialog-input]');
    var error = dialog.querySelector('[data-wishlist-dialog-error]');

    dialogHasInput = !!opts.input;
    dialogCallback = opts.onConfirm || null;

    dialog.querySelector('[data-wishlist-dialog-title]').textContent = opts.title || '';
    dialog.querySelector('[data-wishlist-dialog-confirm]').textContent = opts.confirmText || 'OK';
    dialog.querySelector('[data-wishlist-dialog-label]').textContent = opts.label || '';

    msgEl.textContent = opts.message || '';
    msgEl.hidden = !opts.message;

    field.hidden = !dialogHasInput;
    error.hidden = true;
    input.value = opts.value || '';

    dialog.classList.add('is-open');
    document.body.classList.add('wishlistsh-modal-open');

    if (dialogHasInput) {
      setTimeout(function () { input.focus(); input.select(); }, 50);
    }
  }

  function closeDialog() {
    var dialog = document.querySelector('[data-wishlist-dialog]');
    if (dialog) dialog.classList.remove('is-open');
    document.body.classList.remove('wishlistsh-modal-open');
    dialogCallback = null;
  }

  function confirmDialog() {
    var dialog = document.querySelector('[data-wishlist-dialog]');
    if (!dialog) return;
    var value = '';
    if (dialogHasInput) {
      value = dialog.querySelector('[data-wishlist-dialog-input]').value.trim();
      if (!value) {
        dialog.querySelector('[data-wishlist-dialog-error]').hidden = false;
        return;
      }
    }
    var cb = dialogCallback;
    closeDialog();
    if (cb) cb(value);
  }

  /* ---------------- toast ---------------- */

  function showWishlistMessage(text) {
    var el = document.querySelector('[data-wishlist-message]');
    if (!el) {
      el = document.createElement('div');
      el.setAttribute('data-wishlist-message', '');
      el.className = 'wishlistsh__message';
      document.body.appendChild(el);
    }
    el.textContent = text;
    el.classList.add('is-visible');
    clearTimeout(el._hideTimeout);
    el._hideTimeout = setTimeout(function () { el.classList.remove('is-visible'); }, 3000);
  }

  /* ---------------- heart click ---------------- */

  function handleToggleClick(e) {
    var btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    var id = btn.getAttribute('data-id');

    if (isInWishlist(id)) {
      removeFromWishlist(id);
      showWishlistMessage('Removed from wishlist');
      return;
    }

    openModal({
      id: id,
      variantId: btn.getAttribute('data-variant-id'),
      handle: btn.getAttribute('data-handle'),
      title: btn.getAttribute('data-title'),
      url: btn.getAttribute('data-url'),
      image: btn.getAttribute('data-image'),
      price: btn.getAttribute('data-price'),
      comparePrice: btn.getAttribute('data-compare-price') || '',
      sku: btn.getAttribute('data-sku') || ''
    });
  }

  /* ---------------- wishlist page rendering ---------------- */

  function buildCardMarkup(item) {
    var priceHtml = item.comparePrice
      ? '<span class="cardsh__price-sale">' + item.price + '</span>' +
        '<span class="cardsh__price-compare">' + item.comparePrice + '</span>'
      : '<span class="cardsh__price-regular">' + item.price + '</span>';

    var skuHtml = item.sku ? '<p class="cardsh__sku">' + item.sku + '</p>' : '';

    return (
      '<div class="cardsh__card" data-wishlist-card data-product-id="' + item.id + '">' +
        '<div class="cardsh__media-wrap">' +
          '<a href="' + item.url + '" class="cardsh__media-link" aria-label="' + item.title + '">' +
            '<div class="cardsh__media">' +
              '<img class="cardsh__image cardsh__image--primary" src="' + item.image + '" alt="' + item.title + '" loading="lazy">' +
            '</div>' +
          '</a>' +
          '<button type="button" class="cardsh__wishlist-btn is-active" data-wishlist-toggle ' +
            'data-id="' + item.id + '" data-variant-id="' + item.variantId + '" data-handle="' + item.handle + '" ' +
            'data-title="' + item.title + '" data-url="' + item.url + '" data-image="' + item.image + '" ' +
            'data-price="' + item.price + '" data-compare-price="' + item.comparePrice + '" data-sku="' + item.sku + '" ' +
            'aria-label="Remove from wishlist">' +
            '<svg xmlns="http://www.w3.org/2000/svg" width="17" height="15" viewBox="0 0 17 15" fill="none">' +
              '<path d="M7.61667 2.41667L8.25 3.25L8.88333 2.41667C9.61667 1.45 10.7 0.75 12 0.75C14.075 0.75 15.75 2.425 15.75 4.5C15.75 5.275 15.5167 5.99167 15.1167 6.58333C14.4417 7.59167 8.25 14.0833 8.25 14.0833C8.25 14.0833 2.05833 7.59167 1.38333 6.58333C0.983333 5.99167 0.75 5.275 0.75 4.5C0.75 2.425 2.425 0.75 4.5 0.75C5.8 0.75 6.88333 1.45 7.61667 2.41667Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
            '</svg>' +
          '</button>' +
        '</div>' +
        '<div class="cardsh__info">' +
          '<a href="' + item.url + '" class="cardsh__title-link"><p class="cardsh__title">' + item.title + '</p></a>' +
          skuHtml +
          '<p class="cardsh__price">' + priceHtml + '</p>' +
          '<button type="button" class="cardsh__atc-btn" data-wishlist-atc-single data-variant-id="' + item.variantId + '">Add to cart</button>' +
        '</div>' +
      '</div>'
    );
  }

  function renderSidebar() {
    var sidebar = document.querySelector('[data-wishlist-sidebar]');
    if (!sidebar) return;

    var data = getWishlistData();
    var categories = Object.keys(data);
    if (!activeCategory || !data[activeCategory]) activeCategory = categories[0] || null;

    sidebar.innerHTML = categories.map(function (cat) {
      var isActive = cat === activeCategory;
      return '<button type="button" class="wishlistsh__sidebar-item' + (isActive ? ' is-active' : '') + '" data-wishlist-sidebar-select="' + cat.replace(/"/g, '&quot;') + '">' +
        '<span class="wishlistsh__sidebar-item-name">' + cat + '</span>' +
        '<span class="wishlistsh__sidebar-item-count">' + data[cat].length + ' Items</span>' +
      '</button>';
    }).join('');
  }

  function renderContent() {
    var grid = document.querySelector('[data-wishlist-grid]');
    if (!grid) return;

    var empty = document.querySelector('[data-wishlist-empty]');
    var head = document.querySelector('[data-wishlist-content-head]');
    var heading = document.querySelector('[data-wishlist-category-heading]');
    var countLabel = document.querySelector('[data-wishlist-category-count]');
    var addAllBtn = document.querySelector('[data-wishlist-add-all]');

    var data = getWishlistData();
    var categories = Object.keys(data);
    if (!activeCategory || !data[activeCategory]) activeCategory = categories[0] || null;

    var items = activeCategory ? data[activeCategory] : [];
    var hasItems = items.length > 0;

    if (head) head.hidden = !activeCategory;
    if (heading) heading.textContent = activeCategory || '';
    if (countLabel) countLabel.textContent = items.length + (items.length === 1 ? ' Item' : ' Items');

    grid.innerHTML = items.map(buildCardMarkup).join('');
    grid.hidden = !hasItems;
    if (empty) empty.hidden = !!categories.length;
    if (addAllBtn) addAllBtn.hidden = !hasItems;
  }

  function renderWishlistPage() {
    renderSidebar();
    renderContent();
  }

  /* ---------------- cart actions ---------------- */

  function cartRoot() {
    return window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
  }

  /**
   * Adds variants to the cart, then tells Horizon so the cart drawer
   * re-renders and opens (only after the add succeeded).
   */
  async function addVariantsToCart(variantIds, sourceEl) {
    var events = null;
    try {
      events = await import('@shopify/events');
    } catch (err) {
      console.warn('[wishlist-sh] could not load @shopify/events', err);
    }

    var sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach(function (el) {
      if (el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    });

    var body = {
      items: variantIds.map(function (id) { return { id: parseInt(id, 10), quantity: 1 }; })
    };
    if (sectionIds.length) body.sections = sectionIds.join(',');

    var res = await fetch(cartRoot() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    var data = await res.json();

    if (!res.ok || data.status) {
      throw new Error(data.description || data.message || 'Could not add to cart');
    }

    var cart = await fetch(cartRoot() + 'cart.js').then(function (r) { return r.json(); });

    // Product(s) are in the cart now: notify the drawer + header (this opens the drawer)
    if (events) {
      var deferred = events.CartLinesUpdateEvent.createPromise();
      var target = sourceEl && sourceEl.isConnected ? sourceEl : document.body;

      target.dispatchEvent(
        new events.CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: variantIds.map(function (id) { return { merchandiseId: String(id), quantity: 1 }; }),
          promise: deferred.promise
        })
      );

      deferred.resolve({
        cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'wishlist-sh',
          sourceId: 'wishlist-sh',
          itemCount: variantIds.length,
          sections: data.sections,
          didError: false
        }
      });
    }

    // Safety net: always open the drawer, even if "auto-open" is off in theme settings
    setTimeout(function () {
      var drawer = document.getElementById('cart-drawer');
      if (drawer && !drawer.isOpen && typeof drawer.open === 'function') drawer.open();
    }, 300);

    return cart;
  }

  function addSingleToCart(btn) {
    var variantId = btn.getAttribute('data-variant-id');
    btn.disabled = true;
    btn.classList.add('is-loading');

    addVariantsToCart([variantId], btn)
      .catch(function (err) {
        showWishlistMessage((err && err.message) || 'Could not add to cart');
      })
      .finally(function () {
        btn.disabled = false;
        btn.classList.remove('is-loading');
      });
  }

  function addAllToCart() {
    if (!activeCategory) return;
    var items = getCategoryItems(activeCategory);
    if (!items.length) return;

    var addAllBtn = document.querySelector('[data-wishlist-add-all]');
    if (addAllBtn) { addAllBtn.disabled = true; addAllBtn.classList.add('is-loading'); }

    var variantIds = items.map(function (item) { return item.variantId; });

    addVariantsToCart(variantIds, addAllBtn)
      .then(function () {
        deleteCategory(activeCategory);
        activeCategory = null;
        renderWishlistPage();
      })
      .catch(function (err) {
        showWishlistMessage((err && err.message) || 'Some products could not be added to cart');
      })
      .finally(function () {
        if (addAllBtn) { addAllBtn.disabled = false; addAllBtn.classList.remove('is-loading'); }
      });
  }

  /* ---------------- init ---------------- */

  function init() {
    updateCountBadges();
    syncCardStates();
    renderWishlistPage();

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-wishlist-dialog-close]')) { closeDialog(); return; }
      if (e.target.closest('[data-wishlist-dialog-confirm]')) { confirmDialog(); return; }

      handleToggleClick(e);

      var selectBtn = e.target.closest('[data-wishlist-modal-select]');
      if (selectBtn && pendingItem) {
        addToCategory(selectBtn.getAttribute('data-wishlist-modal-select'), pendingItem);
        showWishlistMessage('Added to wishlist');
        closeModal();
        return;
      }

      var newBtn = e.target.closest('[data-wishlist-modal-new-btn]');
      if (newBtn) {
        newBtn.hidden = true;
        var form = document.querySelector('[data-wishlist-modal-new-form]');
        if (form) {
          form.hidden = false;
          var input = form.querySelector('[data-wishlist-modal-new-input]');
          if (input) input.focus();
        }
        return;
      }

      var confirmBtn = e.target.closest('[data-wishlist-modal-new-confirm]');
      if (confirmBtn && pendingItem) {
        var input2 = document.querySelector('[data-wishlist-modal-new-input]');
        var name = input2 ? input2.value.trim() : '';
        if (!name) return;
        createCategory(name);
        addToCategory(name, pendingItem);
        showWishlistMessage('Added to wishlist');
        closeModal();
        return;
      }

      if (e.target.closest('[data-wishlist-modal-close]')) { closeModal(); return; }

      var pageNewListBtn = e.target.closest('[data-wishlist-new-list-page]');
      if (pageNewListBtn) {
        openDialog({
          title: 'New list',
          label: 'List name',
          input: true,
          confirmText: 'Create',
          onConfirm: function (listName) {
            createCategory(listName);
            activeCategory = listName;
            renderWishlistPage();
          }
        });
        return;
      }

      var sidebarBtn = e.target.closest('[data-wishlist-sidebar-select]');
      if (sidebarBtn) {
        activeCategory = sidebarBtn.getAttribute('data-wishlist-sidebar-select');
        renderContent();
        document.querySelectorAll('[data-wishlist-sidebar-select]').forEach(function (b) {
          b.classList.toggle('is-active', b === sidebarBtn);
        });
        return;
      }

      var editBtn = e.target.closest('[data-wishlist-edit-category]');
      if (editBtn && activeCategory) {
        var oldName = activeCategory;
        openDialog({
          title: 'Rename list',
          label: 'List name',
          input: true,
          value: oldName,
          confirmText: 'Save',
          onConfirm: function (newName) {
            if (newName === oldName) return;
            if (renameCategory(oldName, newName)) {
              activeCategory = newName;
              renderWishlistPage();
            } else {
              showWishlistMessage('A list with that name already exists');
            }
          }
        });
        return;
      }

      var deleteBtn = e.target.closest('[data-wishlist-delete-category]');
      if (deleteBtn && activeCategory) {
        var delName = activeCategory;
        openDialog({
          title: 'Delete list',
          message: 'Delete "' + delName + '" and all its items?',
          input: false,
          confirmText: 'Delete',
          onConfirm: function () {
            deleteCategory(delName);
            activeCategory = null;
            renderWishlistPage();
          }
        });
        return;
      }

      var addAllBtn = e.target.closest('[data-wishlist-add-all]');
      if (addAllBtn) { e.preventDefault(); addAllToCart(); return; }

      var singleBtn = e.target.closest('[data-wishlist-atc-single]');
      if (singleBtn) { e.preventDefault(); addSingleToCart(singleBtn); }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && e.target.matches('[data-wishlist-dialog-input]')) {
        e.preventDefault();
        confirmDialog();
        return;
      }
      if (e.key === 'Escape') {
        var dlg = document.querySelector('[data-wishlist-dialog].is-open');
        if (dlg) { closeDialog(); return; }
      }
      if (e.key === 'Enter' && e.target.matches('[data-wishlist-modal-new-input]')) {
        e.preventDefault();
        var confirmBtn = document.querySelector('[data-wishlist-modal-new-confirm]');
        if (confirmBtn) confirmBtn.click();
      }
    });

    document.addEventListener('wishlist-sh:updated', function () {
      updateCountBadges();
      syncCardStates();
      renderWishlistPage();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();